import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import Dropdown from 'components/@common/Inputs/Dropdown';
import { ModalContainer, ModalContent, ModalHeader, ModalFooter } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import GET_CLASSROOM from 'graph/classroom/get-classroom-query';
import GET_MY_CLASSROOMS from 'graph/classroom/get-my-classrooms-query';
import TRANSFER_STUDENT from 'graph/student/transfer-student-mutation';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import { makeStyles, TextField, Typography } from '@material-ui/core';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import { AlertVariant } from 'types/enums';
import { Classroom, Student } from 'types/schema-generated-types';
import { getFullName } from 'utilities/name-utils';

type TransferStudentModalProps = {
  onSubmitSuccess: () => void;
};

const TransferStudentModal: React.FC<TransferStudentModalProps> = ({ onSubmitSuccess }) => {
  const classes = useStyles();
  const { t } = useTranslation('TransferStudentModal');
  const [, { closeModal, openAlert }] = useUiStore();
  const [{ me }] = useUserStore();
  const [toClassroomId, setSelectedToClassroomId] = useState(null);
  const { studentId, classroomId } = useParseActionRouteParams();
  const { data, loading: classroomLoading, error: getClassroomError } = useQuery(GET_CLASSROOM, {
    variables: { id: Number(classroomId) }
  });
  const { data: classroomsData, loading: classroomsLoading, error: getClassroomsError } = useQuery(
    GET_MY_CLASSROOMS,
    {
      variables: { userId: me?.id }
    }
  );
  const [transferStudent, { loading: transferStudentMutationLoading }] = useMutation(
    TRANSFER_STUDENT,
    {
      refetchQueries: [
        {
          query: GET_CLASSROOM,
          variables: { id: Number(classroomId) }
        },
        {
          query: GET_CLASSROOM,
          variables: { id: Number(toClassroomId) }
        }
      ]
    }
  );

  const student = data?.classroom?.students?.find((x: Student) => x.id === Number(studentId));
  const studentName = student ? getFullName(student) : '';
  const getClassrooms = useCallback(
    () =>
      classroomsData?.classrooms.map((x: Classroom) => ({
        label: `${x.name}  (${x.organization?.name})`,
        value: x.id
      })),
    [classroomsData]
  );
  const classrooms = getClassrooms();
  const loading = classroomLoading || classroomsLoading || transferStudentMutationLoading;
  const error = getClassroomError || getClassroomsError;

  const handleCancel = (): void => {
    closeModal();
  };

  const handleTransfer = async (): Promise<void> => {
    await transferStudent({
      variables: {
        where: { studentId: Number(studentId) },
        data: { fromClassroomId: Number(classroomId), toClassroomId }
      }
    });

    onSubmitSuccess();
  };

  const handleSubmit = async (): Promise<void> => {
    openAlert({
      variant: AlertVariant.WARNING,
      title: t(
        'Warning! You are attempting to transfer the following student from one class to another:'
      ),
      text: studentName,
      onSubmit: handleTransfer,
      confirmButtonText: t('Yes, Transfer Student')
    });
  };

  const handleChange = (e: any) => {
    setSelectedToClassroomId(e.target.value);
  };

  return (
    <PageContainer
      loading={loading}
      error={error}
      showChildrenWhileLoading={transferStudentMutationLoading}
    >
      <ModalContainer
        Footer={
          <ModalFooter
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            submitDisabled={!toClassroomId}
          />
        }
      >
        <ModalHeader>
          <Typography variant="h2" component="h1">
            {t('Transfer Student')} - <b>{studentName}</b>
          </Typography>
        </ModalHeader>
        <ModalContent>
          <div>
            <TextField
              type="text"
              fullWidth
              autoComplete="off"
              variant={'outlined'}
              size={'small'}
              disabled={true}
              value={data?.classroom?.name}
              label={t('Transfer From')}
            />
          </div>
          <div className={classes.spacingY}>
            <Dropdown
              id="classrooms"
              options={classrooms ?? []}
              handleChange={handleChange}
              label={t('Transfer To')}
              value={toClassroomId || ''}
            />
          </div>
        </ModalContent>
      </ModalContainer>
    </PageContainer>
  );
};

const useStyles = makeStyles(() => ({
  spacingY: {
    marginTop: 30,
    marginBottom: 30
  }
}));

export default TransferStudentModal;
